import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Plus, Trash2, Edit, Check, X, ClipboardList, GripVertical, ChevronDown, ChevronRight } from 'lucide-react';

const SkillFormsPage = () => {
    const { getAuthHeaders, API } = useAuth();
    const [forms, setForms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingForm, setEditingForm] = useState(null); // null = list view, object = editing

    useEffect(() => { fetchForms(); }, []);

    const fetchForms = async () => {
        try {
            const res = await axios.get(`${API}/skill-forms`, { headers: getAuthHeaders() });
            setForms(res.data);
        } catch { toast.error('Failed to load skill forms'); }
        finally { setLoading(false); }
    };

    const createForm = async () => {
        try {
            const res = await axios.post(`${API}/skill-forms`, {
                name: 'New Skill Form',
                skills: [{ id: crypto.randomUUID(), name: 'Skill 1', criteria: [{ id: crypto.randomUUID(), name: 'Criterion 1' }] }],
                grading_scale: ['NYC', 'C']
            }, { headers: getAuthHeaders() });
            setEditingForm(res.data);
            fetchForms();
            toast.success('Skill form created');
        } catch { toast.error('Failed to create'); }
    };

    const deleteForm = async (id) => {
        if (!window.confirm('Delete this skill form?')) return;
        try {
            await axios.delete(`${API}/skill-forms/${id}`, { headers: getAuthHeaders() });
            setForms(f => f.filter(x => x.id !== id));
            toast.success('Deleted');
        } catch { toast.error('Failed to delete'); }
    };

    const publishForm = async (id, status) => {
        try {
            await axios.put(`${API}/skill-forms/${id}`, { status }, { headers: getAuthHeaders() });
            setForms(f => f.map(x => x.id === id ? { ...x, status } : x));
            toast.success(status === 'published' ? 'Published' : 'Unpublished');
        } catch { toast.error('Failed to update status'); }
    };

    if (editingForm) {
        return <SkillFormEditor form={editingForm} API={API} getAuthHeaders={getAuthHeaders} onClose={() => { setEditingForm(null); fetchForms(); }} />;
    }

    return (
        <Layout>
            <div className="space-y-6 page-enter" data-testid="skill-forms-page">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-3xl font-bold text-ica-navy">Skill Forms</h1>
                        <p className="text-gray-500 mt-1">Simplified rubrics for skill assessments</p>
                    </div>
                    <Button onClick={createForm} data-testid="create-skill-form-btn">
                        <Plus className="w-4 h-4 mr-2" /> New Skill Form
                    </Button>
                </div>

                {loading ? (
                    <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="skeleton h-16 rounded-xl" />)}</div>
                ) : forms.length === 0 ? (
                    <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                        <ClipboardList className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                        <p className="text-gray-500">No skill forms yet. Create one to get started.</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {forms.map(form => (
                            <div key={form.id} className="bg-white rounded-lg border border-gray-200 p-4 flex items-center justify-between hover:shadow-md transition-shadow cursor-pointer"
                                onClick={() => setEditingForm(form)} data-testid={`skill-form-${form.id}`}>
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 bg-ica-gold/10 rounded-lg flex items-center justify-center">
                                        <ClipboardList className="w-5 h-5 text-ica-gold" />
                                    </div>
                                    <div>
                                        <p className="font-semibold text-gray-900">{form.name}</p>
                                        <p className="text-sm text-gray-500">{form.skills?.length || 0} skills &bull; {form.grading_scale?.join(', ')}</p>
                                    </div>
                                    <span className={`ml-2 text-xs px-2 py-0.5 rounded-full font-medium ${form.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                        {form.status || 'draft'}
                                    </span>
                                </div>
                                <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-ica-navy dark:text-slate-400 dark:hover:text-ica-gold"
                                        onClick={() => publishForm(form.id, form.status === 'published' ? 'draft' : 'published')}
                                        title={form.status === 'published' ? 'Unpublish' : 'Publish'}>
                                        {form.status === 'published' ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                                    </Button>
                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-400 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300"
                                        onClick={() => deleteForm(form.id)} title="Delete Form">
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Layout>
    );
};

// ========== Simplified Skill Form Editor ==========
const SkillFormEditor = ({ form, API, getAuthHeaders, onClose }) => {
    const [name, setName] = useState(form.name || '');
    const [description, setDescription] = useState(form.description || '');
    const [skills, setSkills] = useState(form.skills || []);
    const [gradingScale, setGradingScale] = useState(form.grading_scale || ['NYC', 'C']);
    const [scaleInput, setScaleInput] = useState('');
    const [saving, setSaving] = useState(false);
    const [expandedSkill, setExpandedSkill] = useState(skills[0]?.id || null);

    const save = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/skill-forms/${form.id}`, {
                name: name.trim(),
                description: description.trim() || null,
                skills,
                grading_scale: gradingScale
            }, { headers: getAuthHeaders() });
            toast.success('Saved');
        } catch { toast.error('Failed to save'); }
        finally { setSaving(false); }
    };

    const addSkill = () => {
        const newSkill = { id: crypto.randomUUID(), name: '', criteria: [{ id: crypto.randomUUID(), name: '' }] };
        setSkills([...skills, newSkill]);
        setExpandedSkill(newSkill.id);
    };

    const removeSkill = (skillId) => {
        setSkills(skills.filter(s => s.id !== skillId));
    };

    const updateSkillName = (skillId, newName) => {
        setSkills(skills.map(s => s.id === skillId ? { ...s, name: newName } : s));
    };

    const addCriterion = (skillId) => {
        setSkills(skills.map(s => s.id === skillId ? { ...s, criteria: [...s.criteria, { id: crypto.randomUUID(), name: '' }] } : s));
    };

    const removeCriterion = (skillId, critId) => {
        setSkills(skills.map(s => s.id === skillId ? { ...s, criteria: s.criteria.filter(c => c.id !== critId) } : s));
    };

    const updateCriterionName = (skillId, critId, newName) => {
        setSkills(skills.map(s => s.id === skillId ? { ...s, criteria: s.criteria.map(c => c.id === critId ? { ...c, name: newName } : c) } : s));
    };

    const addScaleItem = () => {
        const val = scaleInput.trim();
        if (val && !gradingScale.includes(val)) {
            setGradingScale([...gradingScale, val]);
            setScaleInput('');
        }
    };

    const removeScaleItem = (idx) => {
        setGradingScale(gradingScale.filter((_, i) => i !== idx));
    };

    return (
        <Layout>
            <div className="space-y-6 page-enter" data-testid="skill-form-editor">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Button variant="ghost" size="icon" onClick={onClose} className="text-slate-500 hover:text-ica-navy dark:text-slate-400 dark:hover:text-ica-gold" title="Back">
                            <X className="w-5 h-5" />
                        </Button>
                        <div>
                            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Form name"
                                className="text-xl font-bold border-none shadow-none p-0 h-auto focus-visible:ring-0" data-testid="skill-form-name" />
                            <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Description (optional)"
                                className="text-sm text-gray-500 border-none shadow-none p-0 h-auto mt-1 focus-visible:ring-0" />
                        </div>
                    </div>
                    <Button onClick={save} disabled={saving} data-testid="save-skill-form-btn">
                        {saving ? 'Saving...' : 'Save'}
                    </Button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Skills & Criteria */}
                    <div className="lg:col-span-2 space-y-4">
                        <div className="flex items-center justify-between">
                            <h2 className="font-semibold text-gray-900">Skills & Criteria</h2>
                            <Button variant="outline" size="sm" onClick={addSkill} data-testid="add-skill-btn">
                                <Plus className="w-4 h-4 mr-1" /> Add Skill
                            </Button>
                        </div>

                        {skills.map((skill, sIdx) => (
                            <div key={skill.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                                {/* Skill header */}
                                <div className="flex items-center gap-2 p-3 bg-gray-50 border-b border-gray-200 cursor-pointer"
                                    onClick={() => setExpandedSkill(expandedSkill === skill.id ? null : skill.id)}>
                                    <GripVertical className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                    {expandedSkill === skill.id ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                                    <Input value={skill.name} onChange={e => updateSkillName(skill.id, e.target.value)}
                                        placeholder={`Skill ${sIdx + 1}`} onClick={e => e.stopPropagation()}
                                        className="flex-1 font-medium border-none shadow-none bg-transparent p-0 h-auto focus-visible:ring-0" data-testid={`skill-name-${sIdx}`} />
                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600 flex-shrink-0"
                                        onClick={(e) => { e.stopPropagation(); removeSkill(skill.id); }} title="Remove Skill">
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                </div>

                                {/* Criteria list */}
                                {expandedSkill === skill.id && (
                                    <div className="p-3 space-y-2">
                                        {skill.criteria.map((crit, cIdx) => (
                                            <div key={crit.id} className="flex items-center gap-2">
                                                <span className="text-xs text-gray-400 w-6 text-center">{cIdx + 1}</span>
                                                <Input value={crit.name} onChange={e => updateCriterionName(skill.id, crit.id, e.target.value)}
                                                    placeholder={`Criterion ${cIdx + 1}`}
                                                    className="flex-1 h-9 text-sm" data-testid={`criterion-${sIdx}-${cIdx}`} />
                                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
                                                    onClick={() => removeCriterion(skill.id, crit.id)} title="Remove Criterion">
                                                    <X className="w-3.5 h-3.5" />
                                                </Button>
                                            </div>
                                        ))}
                                        <Button variant="ghost" size="sm" className="text-ica-gold hover:text-ica-gold-dark mt-1"
                                            onClick={() => addCriterion(skill.id)} data-testid={`add-criterion-${sIdx}`}>
                                            <Plus className="w-3.5 h-3.5 mr-1" /> Add Criterion
                                        </Button>
                                    </div>
                                )}
                            </div>
                        ))}

                        {skills.length === 0 && (
                            <div className="bg-white rounded-xl border border-dashed border-gray-300 p-8 text-center">
                                <p className="text-gray-500">No skills added yet. Click "Add Skill" to begin.</p>
                            </div>
                        )}
                    </div>

                    {/* Grading Scale Panel */}
                    <div className="space-y-4">
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                            <h3 className="font-semibold text-gray-900 mb-3">Grading Scale</h3>
                            <div className="space-y-2">
                                {gradingScale.map((item, idx) => (
                                    <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                                        <span className="flex-1 text-sm font-medium">{item}</span>
                                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-600"
                                            onClick={() => removeScaleItem(idx)} title="Remove">
                                            <X className="w-3 h-3" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                            <div className="flex gap-2 mt-3">
                                <Input value={scaleInput} onChange={e => setScaleInput(e.target.value)}
                                    placeholder="e.g. E (Excellent)" className="flex-1 h-9 text-sm"
                                    onKeyDown={e => e.key === 'Enter' && addScaleItem()} data-testid="scale-input" />
                                <Button variant="outline" size="sm" onClick={addScaleItem}>Add</Button>
                            </div>
                        </div>

                        {/* Preview */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                            <h3 className="font-semibold text-gray-900 mb-3">Preview</h3>
                            {skills.length > 0 ? (
                                <div className="space-y-3">
                                    {skills.filter(s => s.name).map(skill => (
                                        <div key={skill.id}>
                                            <p className="text-sm font-bold text-ica-navy mb-1">{skill.name}</p>
                                            {skill.criteria.filter(c => c.name).map(crit => (
                                                <div key={crit.id} className="flex items-center gap-2 ml-3 mb-1">
                                                    <span className="text-xs text-gray-600">{crit.name}</span>
                                                    <div className="flex gap-1 ml-auto">
                                                        {gradingScale.map((g, i) => (
                                                            <span key={i} className="text-[10px] px-1.5 py-0.5 bg-gray-100 rounded text-gray-500">{g}</span>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-gray-400">Add skills to preview</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default SkillFormsPage;
